import { Label } from "design-system/atoms/Label"
import { Textarea } from "design-system/atoms/Textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Loading } from "modules/shared/components/Loading"
import { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityResponseDataSchema,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useCapabilityProvider } from "../../hooks/useCapabilityProvider"
import { useInstallCapabilityMutation } from "../../hooks/useInstallCapabilityMutation"
import { useUpdateCapabilityConfigMutation } from "../../hooks/useUpdateCapabilityConfigMutation"
import { CapabilityOrganizationPhoneNumbers } from "./CapabilityOrganizationPhoneNumbers"

type Props = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: {
    provider?: string
    activationInstructions?: string
    responseInstructions?: string
  }
  onClose: () => void
  onPendingChange?: (isPending: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
  onSubmitHandlerRef?: React.MutableRefObject<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >
}

export function GetOrderTrackingForm({
  capability,
  mode = "install",
  initialConfig,
  onClose,
  onPendingChange,
  onConfigure,
  onSubmitHandlerRef,
}: Props) {
  const { t } = useTranslation()
  const [activationInstructions, setActivationInstructions] = useState(
    initialConfig?.activationInstructions ?? "",
  )
  const [responseInstructions, setResponseInstructions] = useState(
    initialConfig?.responseInstructions ?? "",
  )
  const { mutateAsync: installCapability, isPending: isInstallPending } =
    useInstallCapabilityMutation()
  const { mutateAsync: updateCapabilityConfig, isPending: isUpdatePending } =
    useUpdateCapabilityConfigMutation()

  const isPending = mode === "install" ? isInstallPending : isUpdatePending

  const {
    provider,
    setProvider,
    providerOptions,
    isIntegrationsStatusPending,
    organizationPhoneNumbers,
    setOrganizationPhoneNumbers,
  } = useCapabilityProvider({
    capability,
    mode,
    initialConfig,
  })

  useEffect(() => {
    onPendingChange?.(isPending || isIntegrationsStatusPending)
  }, [isPending, isIntegrationsStatusPending, onPendingChange])

  useEffect(() => {
    setActivationInstructions(initialConfig?.activationInstructions ?? "")
  }, [initialConfig?.activationInstructions])

  useEffect(() => {
    setResponseInstructions(initialConfig?.responseInstructions ?? "")
  }, [initialConfig?.responseInstructions])

  const handleSubmit = useCallback(
    async (
      campaignAssignation: CapabilityCampaignAssignation,
      campaignIds: string[],
      campaignFlow?: "Inbound" | "Outbound",
    ) => {
      if (isPending) return

      let installedCapability: z.infer<typeof CapabilityResponseDataSchema> | undefined

      if (mode === "install") {
        installedCapability = await installCapability({
          identifier: capability.identifier,
          config: {
            provider,
            organizationPhoneNumbers,
            activationInstructions,
            responseInstructions,
          },
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })

        toast.success(
          t("capabilities.list.install.success", {
            capability: t(`capabilities.catalog.${capability.identifier}.name`),
          }),
        )
      } else {
        await updateCapabilityConfig({
          id: capability.id,
          config: {
            provider,
            organizationPhoneNumbers,
            activationInstructions,
            responseInstructions,
          },
        })
      }

      if (onConfigure && installedCapability) {
        onConfigure(installedCapability)
      } else {
        onClose()
      }
    },
    [
      isPending,
      mode,
      onConfigure,
      installCapability,
      capability.identifier,
      capability.id,
      provider,
      t,
      updateCapabilityConfig,
      onClose,
      organizationPhoneNumbers,
      activationInstructions,
      responseInstructions,
    ],
  )

  if (onSubmitHandlerRef) {
    onSubmitHandlerRef.current = handleSubmit
  }

  if (isIntegrationsStatusPending) return <Loading />

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <Label>{t(`capabilities.catalog.${capability.identifier}.config.provider`)}</Label>
        <Select value={provider ?? undefined} onValueChange={setProvider}>
          <SelectTrigger>
            <SelectValue
              placeholder={t(`capabilities.catalog.${capability.identifier}.config.placeholder`)}
            />
          </SelectTrigger>
          <SelectContent>
            {providerOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>
          {t("capabilities.catalog.GetOrderTracking.config.activationInstructions.label")}
        </Label>
        <Textarea
          value={activationInstructions}
          onChange={(ev) => setActivationInstructions(ev.target.value)}
          placeholder={t(
            "capabilities.catalog.GetOrderTracking.config.activationInstructions.placeholder",
          )}
          rows={5}
          maxLength={2048}
        />
        <p className="text-sm text-muted-foreground">
          {t("capabilities.catalog.GetOrderTracking.config.activationInstructions.description")}
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label>
          {t("capabilities.catalog.GetOrderTracking.config.responseInstructions.label")}
        </Label>
        <Textarea
          value={responseInstructions}
          onChange={(ev) => setResponseInstructions(ev.target.value)}
          placeholder={t(
            "capabilities.catalog.GetOrderTracking.config.responseInstructions.placeholder",
          )}
          rows={5}
          maxLength={2048}
        />
        <p className="text-sm text-muted-foreground">
          {t("capabilities.catalog.GetOrderTracking.config.responseInstructions.description")}
        </p>
      </div>
      <CapabilityOrganizationPhoneNumbers
        organizationPhoneNumbers={organizationPhoneNumbers}
        setOrganizationPhoneNumbers={setOrganizationPhoneNumbers}
      />
    </div>
  )
}
